import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Menu,
  X,
  Scissors,
  Clock,
  Star,
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  ChevronRight,
  Quote,
  ArrowRight,
  CalendarDays
} from "lucide-react";

const BRAND_NAME = "Salon Bahadır Kersü";

export default function LandingPage() {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, barbersRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/barbers?homepage_only=true`),
          axios.get(`${API}/reviews?limit=6`)
        ]);
        setServices(servicesRes.data);
        setBarbers(barbersRes.data);
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }

      try {
        const portfolioRes = await axios.get(`${API}/portfolio?limit=8`);
        setPortfolio(portfolioRes.data);
      } catch (error) {}
    };
    fetchData();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Navigation */}
      <nav className={`nav-elite ${isScrolled ? "scrolled" : ""}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Scissors className="w-5 h-5 md:w-6 md:h-6 text-[#c9a96e]" />
            <span className="font-display text-base md:text-xl font-semibold text-[#1a1a2e]">{BRAND_NAME}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("services")} className="nav-link">Hizmetler</button>
            {barbers.length > 0 && <button onClick={() => scrollToSection("barbers")} className="nav-link">Ekibimiz</button>}
            <button onClick={() => scrollToSection("portfolio")} className="nav-link">Referanslar</button>
            <button onClick={() => scrollToSection("reviews")} className="nav-link">Yorumlar</button>
            <button onClick={() => scrollToSection("contact")} className="nav-link">İletişim</button>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to={userType === "admin" ? "/admin" : "/dashboard"}>
                <Button className="btn-secondary text-xs py-2 px-5">
                  {userType === "admin" ? "Admin Panel" : "Hesabım"}
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button className="btn-secondary text-xs py-2 px-5">Giriş Yap</Button>
              </Link>
            )}
            <Link to="/booking">
              <Button className="btn-primary text-xs py-2 px-5">Randevu Al</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#1a1a2e] p-1"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <button onClick={() => setMobileMenuOpen(false)} className="absolute top-4 right-4 text-[#1a1a2e] p-2">
          <X className="w-6 h-6" />
        </button>
        <div className="flex flex-col items-center gap-1">
          <button onClick={() => scrollToSection("services")} className="mobile-menu-link">Hizmetler</button>
          {barbers.length > 0 && <button onClick={() => scrollToSection("barbers")} className="mobile-menu-link">Ekibimiz</button>}
          <button onClick={() => scrollToSection("portfolio")} className="mobile-menu-link">Referanslar</button>
          <button onClick={() => scrollToSection("reviews")} className="mobile-menu-link">Yorumlar</button>
          <button onClick={() => scrollToSection("contact")} className="mobile-menu-link">İletişim</button>

          <div className="flex flex-col gap-3 mt-6 w-full max-w-xs">
            <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
              <Button className="btn-accent w-full py-3">
                <CalendarDays className="w-4 h-4 mr-2" />
                Randevu Al
              </Button>
            </Link>
            {user ? (
              <Link to={userType === "admin" ? "/admin" : "/dashboard"} onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-secondary w-full py-3">
                  {userType === "admin" ? "Admin Panel" : "Hesabım"}
                </Button>
              </Link>
            ) : (
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-secondary w-full py-3">Giriş Yap</Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <img
          src="https://images.unsplash.com/photo-1543697506-6729425f7265?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Salon Bahadır Kersü"
          className="hero-bg"
        />
        <div className="hero-overlay"></div>
        <div className="hero-content animate-fade-in">
          <p className="font-accent text-[#c9a96e] text-xs md:text-sm mb-3 md:mb-4 tracking-[0.2em]">
            PREMIUM BERBER DENEYİMİ
          </p>
          <h1 className="font-display text-[2.25rem] sm:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-[1.1]">
            {BRAND_NAME}
          </h1>
          <p className="text-white/70 text-sm md:text-lg max-w-xl mx-auto mb-8 md:mb-10 leading-relaxed px-2">
            Tıraş sanatının en ince detaylarıyla buluşun.
            Profesyonel ekibimiz ile premium bakım deneyimi yaşayın.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center px-4 sm:px-0">
            <Link to="/booking" className="w-full sm:w-auto">
              <Button className="btn-accent text-sm py-3.5 px-8 w-full sm:w-auto">
                Randevu Al
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <button
              onClick={() => scrollToSection("services")}
              className="w-full sm:w-auto py-3.5 px-8 rounded-lg font-semibold text-sm text-white border-2 border-white/30 bg-transparent transition-all hover:bg-white hover:text-[#1a1a2e] active:scale-[0.98]"
            >
              Hizmetlerimiz
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-14 md:py-28 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#c9a96e] font-medium text-xs md:text-sm mb-2 tracking-wider uppercase">Hizmetlerimiz</p>
            <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#1a1a2e]">Premium Hizmetler</h2>
            <div className="w-12 md:w-16 h-0.5 bg-[#c9a96e] mx-auto mt-3"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="service-card animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-display text-lg md:text-xl text-[#1a1a2e] font-semibold">{service.name}</h3>
                  <span className="service-price">{service.price}₺</span>
                </div>
                <p className="text-[#6b7280] text-sm mb-3 leading-relaxed">{service.description}</p>
                <div className="flex items-center text-[#9ca3af] text-xs md:text-sm">
                  <Clock className="w-3.5 h-3.5 mr-1.5" />
                  {service.duration_minutes} dakika
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Barbers Section */}
      {barbers.length > 0 && (
        <section id="barbers" className="py-14 md:py-28 px-4 md:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-16">
              <p className="text-[#c9a96e] font-medium text-xs md:text-sm mb-2 tracking-wider uppercase">Ekibimiz</p>
              <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#1a1a2e]">Uzman Berberlerimiz</h2>
              <div className="w-12 md:w-16 h-0.5 bg-[#c9a96e] mx-auto mt-3"></div>
            </div>

            {/* Mobile: horizontal scroll, Desktop: grid */}
            <div className="md:hidden mobile-scroll-container -mx-4 px-4">
              {barbers.map((barber, index) => (
                <div
                  key={barber.id}
                  className="barber-card animate-fade-in w-[70vw] max-w-[280px]"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <img
                    src={barber.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"}
                    alt={barber.name}
                  />
                  <div className="barber-info">
                    <h3 className="font-display text-lg text-white mb-0.5">{barber.name}</h3>
                    <p className="text-[#c9a96e] text-xs font-medium">{barber.specialty}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-8">
              {barbers.map((barber, index) => (
                <div
                  key={barber.id}
                  className="barber-card animate-fade-in"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <img
                    src={barber.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"}
                    alt={barber.name}
                  />
                  <div className="barber-info">
                    <h3 className="font-display text-xl text-white mb-1">{barber.name}</h3>
                    <p className="text-[#c9a96e] text-sm font-medium">{barber.specialty}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Portfolio / References Section */}
      <section id="portfolio" className="py-14 md:py-28 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#c9a96e] font-medium text-xs md:text-sm mb-2 tracking-wider uppercase">Referanslarımız</p>
            <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#1a1a2e]">Çalışmalarımız</h2>
            <div className="w-12 md:w-16 h-0.5 bg-[#c9a96e] mx-auto mt-3"></div>
            <p className="text-[#6b7280] mt-3 text-sm md:text-base max-w-lg mx-auto">En son tıraş ve bakım çalışmalarımızdan örnekler</p>
          </div>

          {portfolio.length > 0 ? (
            <div className="portfolio-grid">
              {portfolio.map((item) => (
                <div key={item.id} className="portfolio-item">
                  <img src={item.image_url} alt={item.title || "Çalışma"} />
                  <div className="portfolio-overlay">
                    <div>
                      <p className="text-white font-semibold text-sm">{item.title}</p>
                      {item.barber_name && <p className="text-white/70 text-xs">{item.barber_name}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
              {[
                "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=400&h=400&fit=crop",
                "https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=400&h=400&fit=crop",
                "https://images.unsplash.com/photo-1621607512214-68297480165e?w=400&h=400&fit=crop",
                "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=400&h=400&fit=crop",
              ].map((url, i) => (
                <div key={i} className="portfolio-item">
                  <img src={url} alt={`Referans ${i + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section id="reviews" className="py-14 md:py-28 px-4 md:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-16">
              <p className="text-[#c9a96e] font-medium text-xs md:text-sm mb-2 tracking-wider uppercase">Yorumlar</p>
              <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#1a1a2e]">Müşteri Deneyimleri</h2>
              <div className="w-12 md:w-16 h-0.5 bg-[#c9a96e] mx-auto mt-3"></div>
            </div>

            {/* Mobile: horizontal scroll */}
            <div className="md:hidden mobile-scroll-container -mx-4 px-4">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className="review-card animate-fade-in w-[80vw] max-w-[300px]"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Quote className="w-6 h-6 text-[#c9a96e] mb-3 opacity-40" />
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 ${i < review.rating ? "text-[#c9a96e] fill-[#c9a96e]" : "text-[#d1d5db]"}`}
                      />
                    ))}
                  </div>
                  <p className="text-[#6b7280] mb-3 italic leading-relaxed text-sm line-clamp-4">"{review.comment}"</p>
                  <p className="text-xs font-semibold text-[#1a1a2e]">{review.customer_name || "Anonim"}</p>
                </div>
              ))}
            </div>

            {/* Desktop: grid */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className="review-card animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Quote className="w-8 h-8 text-[#c9a96e] mb-4 opacity-40" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < review.rating ? "text-[#c9a96e] fill-[#c9a96e]" : "text-[#d1d5db]"}`}
                      />
                    ))}
                  </div>
                  <p className="text-[#6b7280] mb-4 italic leading-relaxed">"{review.comment}"</p>
                  <p className="text-sm font-semibold text-[#1a1a2e]">{review.customer_name || "Anonim"}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-14 md:py-28 px-4 md:px-6 bg-[#1a1a2e] relative overflow-hidden">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="font-display text-2xl md:text-4xl font-semibold text-white mb-4 md:mb-6">
            Premium Deneyim İçin Randevunuzu Alın
          </h2>
          <p className="text-white/60 mb-6 md:mb-8 leading-relaxed text-sm md:text-base">
            Uzman ekibimiz sizi bekliyor. Hemen randevu alın ve farkı yaşayın.
          </p>
          <Link to="/booking">
            <Button className="btn-accent text-sm py-3.5 px-8 w-full sm:w-auto">
              Hemen Randevu Al
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-14 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#c9a96e] font-medium text-xs md:text-sm mb-2 tracking-wider uppercase">İletişim</p>
            <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#1a1a2e]">Bize Ulaşın</h2>
            <div className="w-12 md:w-16 h-0.5 bg-[#c9a96e] mx-auto mt-3"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-5 md:space-y-8">
              <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 md:gap-4 group">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#f5efe6] rounded-lg flex-shrink-0 group-hover:bg-[#c9a96e] transition-colors">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-[#c9a96e] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#1a1a2e] mb-0.5">Adres</h3>
                  <p className="text-[#6b7280] text-sm">Bağdat Caddesi No:123, Kadıköy, İstanbul</p>
                </div>
              </a>

              <a href="tel:+902161234567" className="flex items-start gap-3 md:gap-4 group">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#f5efe6] rounded-lg flex-shrink-0 group-hover:bg-[#c9a96e] transition-colors">
                  <Phone className="w-4 h-4 md:w-5 md:h-5 text-[#c9a96e] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#1a1a2e] mb-0.5">Telefon</h3>
                  <p className="text-[#6b7280] text-sm">+90 (216) 123 45 67</p>
                </div>
              </a>

              <a href="mailto:info@salonbahadirkersu.com" className="flex items-start gap-3 md:gap-4 group">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#f5efe6] rounded-lg flex-shrink-0 group-hover:bg-[#c9a96e] transition-colors">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 text-[#c9a96e] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#1a1a2e] mb-0.5">E-posta</h3>
                  <p className="text-[#6b7280] text-sm">info@salonbahadirkersu.com</p>
                </div>
              </a>

              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#f5efe6] rounded-lg flex-shrink-0">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-[#c9a96e]" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#1a1a2e] mb-0.5">Çalışma Saatleri</h3>
                  <p className="text-[#6b7280] text-sm">Pazartesi - Cumartesi: 09:00 - 19:00</p>
                  <p className="text-[#9ca3af] text-sm">Pazar: Kapalı</p>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#f5efe6] rounded-lg hover:bg-[#c9a96e] transition-all group active:scale-95">
                  <Instagram className="w-4 h-4 md:w-5 md:h-5 text-[#c9a96e] group-hover:text-white" />
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#f5efe6] rounded-lg hover:bg-[#c9a96e] transition-all group active:scale-95">
                  <Facebook className="w-4 h-4 md:w-5 md:h-5 text-[#c9a96e] group-hover:text-white" />
                </a>
              </div>
            </div>

            <div className="h-[300px] md:h-[400px] rounded-xl overflow-hidden border border-[#e5e7eb]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.6504900389396!2d29.0573!3d40.9883!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDU5JzE3LjkiTiAyOcKwMDMnMjYuMyJF!5e0!3m2!1sen!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Salon Bahadır Kersü"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-elite">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-[#c9a96e]" />
              <span className="font-display text-lg font-semibold text-white">{BRAND_NAME}</span>
            </div>

            <div className="flex gap-6 md:gap-8">
              <button onClick={() => scrollToSection("services")} className="text-white/60 hover:text-white text-xs md:text-sm transition-colors cursor-pointer bg-transparent border-none">Hizmetler</button>
              <button onClick={() => scrollToSection("barbers")} className="text-white/60 hover:text-white text-xs md:text-sm transition-colors cursor-pointer bg-transparent border-none">Ekibimiz</button>
              <button onClick={() => scrollToSection("contact")} className="text-white/60 hover:text-white text-xs md:text-sm transition-colors cursor-pointer bg-transparent border-none">İletişim</button>
            </div>

            <p className="text-white/40 text-xs md:text-sm">
              © 2024 {BRAND_NAME}
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="mobile-sticky-cta">
        <Link to="/booking" className="block">
          <Button className="btn-accent w-full py-3 text-sm">
            <CalendarDays className="w-4 h-4 mr-2" />
            Randevu Al
          </Button>
        </Link>
      </div>
    </div>
  );
}
